import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import toast from 'react-hot-toast';
import TranslatedField from './TranslatedField';
import DraftRestoredBanner from './DraftRestoredBanner';
import { useFormDraft, useUnsavedGuard } from '../hooks/useFormDraft';
import { parseVCard, dataUrlToBlob } from '../utils/vcard';
import { DOB_SENTINEL_YEAR } from '../utils/birthday';
import { focusQuiet, focusRow } from '../utils/scroll';
import { Analytics } from '../analytics';
import { backdropClose } from '../utils/modalBackdrop';
import { useCountryText } from '../hooks/useCountryText';

import { uiDocLang } from '../utils/docLang';
// Сплит полного имени по первому пробелу (импорт из vCard/Контактов даёт одно поле).
export function splitName(full) {
  const s = String(full || '').trim().replace(/\s+/g, ' ');
  if (!s) return { first: '', last: '' };
  const i = s.indexOf(' ');
  if (i === -1) return { first: s, last: '' };
  return { first: s.slice(0, i), last: s.slice(i + 1) };
}

// ─── Кастомный DateInput (DD / MM / YYYY) ────────────────────────────────────
// Хранит значение в формате YYYY-MM-DD (ISO, совместимо с БД).
// Три независимых <input> — нет проблем с нативным date-пикером на десктопе.
// Год необязателен: при пустом YYYY пишем год-сентинел (конвенция Apple
// Contacts / vCard, см. DOB_SENTINEL_YEAR). Крон поздравлений сравнивает только
// MM-DD, поэтому фиктивный год не влияет на логику ДР.

// Разбор ISO-строки на части. Сентинел-год показываем как пустой YYYY.
function parseDob(v) {
  if (!v) return { dd: '', mm: '', yyyy: '' };
  const [y, m, d] = v.split('-');
  return { dd: d || '', mm: m || '', yyyy: (y && y !== DOB_SENTINEL_YEAR) ? y : '' };
}

// ISO-строка, которую сейчас отражают поля (пустой/неполный год → сентинел).
function partsToIso(p) {
  if (p.dd && p.mm) {
    const y = (p.yyyy && p.yyyy.length === 4) ? p.yyyy : DOB_SENTINEL_YEAR;
    return `${y}-${p.mm.padStart(2, '0')}-${p.dd.padStart(2, '0')}`;
  }
  return '';
}

function DateInput({ value, onChange, style }) {
  const [parts, setParts] = useState(() => parseDob(value));

  // Синхронизация извне: только если value реально отличается от того, что уже
  // отражают поля. Иначе это эхо нашего же commit (промежуточный сентинел при
  // наборе года) — и ресинк затирал бы год прямо во время ввода.
  const prevValue = useRef(value);
  if (value !== prevValue.current) {
    prevValue.current = value;
    if (value !== partsToIso(parts)) {
      setParts(parseDob(value));
    }
  }

  const mmRef = useRef(null);
  const yyyyRef = useRef(null);

  // Год опционален: partsToIso подставит сентинел при пустом/неполном YYYY.
  const commit = useCallback((next) => {
    onChange(partsToIso(next));
  }, [onChange]);

  // При фокусе выделяем содержимое — печать сразу заменяет значение, но ничего
  // не «исчезает» и не нужно стирать вручную.
  const selectOnFocus = (e) => e.target.select();

  const handleDD = (e) => {
    let v = e.target.value.replace(/\D/g, '').slice(0, 2);
    if (parseInt(v, 10) > 31) v = '31';
    const next = { ...parts, dd: v };
    setParts(next);
    commit(next);
    if (v.length === 2) focusQuiet(mmRef.current);
  };

  const handleMM = (e) => {
    let v = e.target.value.replace(/\D/g, '').slice(0, 2);
    if (parseInt(v, 10) > 12) v = '12';
    const next = { ...parts, mm: v };
    setParts(next);
    commit(next);
    if (v.length === 2) focusQuiet(yyyyRef.current);
  };

  const handleYYYY = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 4);
    const next = { ...parts, yyyy: v };
    setParts(next);
    commit(next);
  };

  const calendarRef = useRef(null);

  // Синхронизируем value скрытого input заранее — до любого клика.
  // Дефолт 1983-01-01 если дата не заполнена.
  useEffect(() => {
    const el = calendarRef.current;
    if (!el) return;
    if (parts.yyyy && parts.mm && parts.dd) {
      el.value = `${parts.yyyy}-${parts.mm.padStart(2,'0')}-${parts.dd.padStart(2,'0')}`;
    } else {
      el.value = '1983-01-01';
    }
  }, [parts.dd, parts.mm, parts.yyyy]);

  const handleCalendarChange = (e) => {
    const v = e.target.value; // YYYY-MM-DD
    if (!v) return;
    const [y, m, d] = v.split('-');
    const next = { dd: d, mm: m, yyyy: y };
    setParts(next);
    commit(next);
  };

  const inputStyle = {
    width: '100%',
    background: 'none',
    border: 'none',
    outline: 'none',
    fontSize: 'inherit',
    color: 'inherit',
    padding: 0,
    textAlign: 'center',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
      <div
        className="form-input"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          cursor: 'text',
          flex: 1,
          ...(style || {}),
        }}
        onClick={() => { if (!parts.dd) focusQuiet(document.getElementById('dob-dd')); }}
      >
        <input
          id="dob-dd"
          inputMode="numeric"
          placeholder="DD"
          value={parts.dd}
          onChange={handleDD}
          onFocus={selectOnFocus}
          style={{ ...inputStyle, width: 28 }}
          maxLength={2}
          data-posthog="client_dob_dd"
        />
        <span style={{ opacity: 0.4, userSelect: 'none' }}>/</span>
        <input
          ref={mmRef}
          inputMode="numeric"
          placeholder="MM"
          value={parts.mm}
          onChange={handleMM}
          onFocus={selectOnFocus}
          style={{ ...inputStyle, width: 28 }}
          maxLength={2}
          data-posthog="client_dob_mm"
        />
        <span style={{ opacity: 0.4, userSelect: 'none' }}>/</span>
        <input
          ref={yyyyRef}
          inputMode="numeric"
          placeholder="YYYY"
          value={parts.yyyy}
          onChange={handleYYYY}
          onFocus={selectOnFocus}
          style={{ ...inputStyle, width: 44 }}
          maxLength={4}
          data-posthog="client_dob_yyyy"
        />
      </div>

      {/* Кнопка-календарь */}
      {/* Обёртка кнопки + скрытый input позиционированы вместе */}
      <div style={{ position: 'relative', flexShrink: 0, display: 'flex' }}>
        <button
          type="button"
          data-posthog="client_dob_calendar_open"
          style={{
            width: 44,
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            background: 'var(--bg3)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text2)',
            padding: 0,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="1" y="3" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M1 7h14" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M5 1v4M11 1v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>

        {/* Нативный date-input поверх кнопки, прозрачный.
            onClick устанавливает дефолтный год до открытия пикера */}
        <input
          ref={calendarRef}
          type="date"
          onChange={handleCalendarChange}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            cursor: 'pointer',
            colorScheme: 'light',
          }}
          tabIndex={-1}
        />
      </div>
    </div>
  );
}

// ─── Сжатие изображения через Canvas ─────────────────────────────────────────
export function compressImage(file, maxSize, quality) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        const ratio = Math.min(maxSize / width, maxSize / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        blob => blob ? resolve(blob) : reject(new Error('Ошибка сжатия')),
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Не удалось загрузить изображение')); };
    img.src = url;
  });
}

async function uploadToR2(presignUrl, blob) {
  const res = await fetch(presignUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'image/jpeg' },
    body: blob,
  });
  if (!res.ok) throw new Error('Ошибка загрузки в хранилище');
}

// ─── Аватар клиента с загрузкой фото ─────────────────────────────────────────
// clientId = null при создании — фото держим локально до Save
function ClientAvatar({ clientId, avatarUrl, previewUrl, onFileSelected, onUploaded, onDeleted, uploading, size = 160, initials }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const displayUrl = previewUrl || avatarUrl;

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!clientId) { onDeleted(); return; }
    try {
      await api.deleteClientAvatar(clientId);
      onDeleted();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <div
        onClick={() => !uploading && inputRef.current?.click()}
        style={{
          width: size, height: size, borderRadius: '50%',
          background: displayUrl ? 'transparent' : 'var(--accent2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: size * 0.36, color: 'white',
          cursor: 'pointer', overflow: 'hidden',
          border: '2px solid var(--border)',
        }}
        title="Нажми чтобы добавить фото"
      >
        {uploading ? (
          <span className="spinner" style={{ width: size * 0.4, height: size * 0.4 }} />
        ) : displayUrl ? (
          <img src={displayUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : clientId ? (
          initials
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '0 16px' }}>
            <span style={{ fontSize: size * 0.22 }}>📷</span>
            <span style={{ fontSize: 13, fontWeight: 600, textAlign: 'center', lineHeight: 1.3 }}>{t('clients.addPhoto')}</span>
          </div>
        )}
      </div>
      {displayUrl && (
        <button
          type="button"
          onClick={handleDelete}
          title="Удалить фото"
          style={{
            position: 'absolute', bottom: 4, right: 4,
            width: 26, height: 26, borderRadius: '50%',
            background: 'var(--red, #ff3b30)', color: 'white',
            border: '2px solid var(--bg2)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, lineHeight: 1, padding: 0,
          }}
        >×</button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={onFileSelected}
      />
    </div>
  );
}

// Загружает блобы в R2 и подтверждает на бэке, возвращает результат
export async function uploadAvatar(clientId, fullBlob, thumbBlob) {
  const [fullPresign, thumbPresign] = await Promise.all([
    api.presignClientAvatar(clientId, 'image/jpeg', 'full'),
    api.presignClientAvatar(clientId, 'image/jpeg', 'thumb'),
  ]);
  await Promise.all([
    uploadToR2(fullPresign.uploadUrl, fullBlob),
    uploadToR2(thumbPresign.uploadUrl, thumbBlob),
  ]);
  return api.confirmClientAvatar(clientId, fullPresign.key, thumbPresign.key);
}

// ─── TranslationHint ──────────────────────────────────────────────────────────
function TranslationHint({ record, field, uiLang }) {
  const { t } = useTranslation();
  if (!record) return null;
  const originalLang = record.original_lang;
  if (!originalLang || originalLang === uiLang) return null;
  const translated = record.translations?.[uiLang]?.[field];
  if (!translated) return null;
  return (
    <div style={{
      marginTop: 4, padding: '6px 10px', background: 'var(--bg3)',
      borderRadius: 6, fontSize: 12, color: 'var(--text2)',
      borderLeft: '2px solid var(--accent)', opacity: 0.85,
    }}>
      <span style={{ fontWeight: 600, opacity: 0.6, marginRight: 4 }}>{t('tasks.showTranslation')}:</span>
      {translated}
    </div>
  );
}

// ─── Кнопка «из адресной книги» внутри модала ────────────────────────────────
// Contact Picker API (iOS/Android) или импорт .vcf (десктоп). Заполняет форму.
function ModalContactsButton({ onFill }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);

  const handleClick = async () => {
    if ('contacts' in navigator && 'ContactsManager' in window) {
      try {
        // icon (фото) поддерживается не везде (Chrome Android — да, Safari iOS — нет)
        let props = ['name', 'tel', 'email'];
        try {
          const supported = await navigator.contacts.getProperties();
          if (supported.includes('icon')) props.push('icon');
        } catch { /* getProperties недоступен — без фото */ }
        const contacts = await navigator.contacts.select(props, { multiple: false });
        if (!contacts || contacts.length === 0) return;
        const c = contacts[0];
        const photoBlob = c.icon?.[0] || null;
        Analytics.clientModalContactImport('picker_api', !!photoBlob);
        onFill({
          name: (c.name?.[0] || '').trim(),
          phone: (c.tel?.[0] || '').trim(),
          email: (c.email?.[0] || '').trim(),
          photoBlob,
        });
      } catch (err) {
        if (err.name !== 'AbortError') toast.error(t('clients.vcfParseError'));
      }
      return;
    }
    // Десктоп — .vcf файл (подсказка при первом использовании)
    if (!localStorage.getItem('vcf_hint_shown')) {
      localStorage.setItem('vcf_hint_shown', '1');
      toast(t('clients.vcfHint'), {
        icon: '💡',
        duration: 8000,
        style: { maxWidth: 340, fontSize: 13, lineHeight: 1.4 },
      });
    }
    fileRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const contacts = parseVCard(await file.text());
      if (contacts.length === 0) {
        Analytics.vcfImportError('modal');
        toast.error(t('clients.vcfParseError'));
        return;
      }
      const { name, phone, email, birthday, photo } = contacts[0];
      Analytics.clientModalContactImport('vcf', !!photo);
      onFill({ name, phone, email, birthday, photoBlob: photo ? dataUrlToBlob(photo) : null });
    } catch {
      Analytics.vcfImportError('modal');
      toast.error(t('clients.vcfParseError'));
    }
  };

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept=".vcf,text/vcard,text/x-vcard"
        style={{ display: 'none' }}
        onChange={handleFile}
      />
      <button
        type="button"
        onClick={handleClick}
        title={t('clients.importContactsTooltip')}
        data-posthog="client_modal_contacts_open"
        style={{
          width: 44,
          flexShrink: 0,
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          background: 'var(--bg3)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text2)',
          padding: 0,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="2" width="18" height="20" rx="3" stroke="currentColor" strokeWidth="1.7"/>
          <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="1.7"/>
          <path d="M6.5 19c0-3.038 2.462-5.5 5.5-5.5s5.5 2.462 5.5 5.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
          <line x1="19" y1="7" x2="21" y2="7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
          <line x1="19" y1="11" x2="21" y2="11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
          <line x1="19" y1="15" x2="21" y2="15" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
        </svg>
      </button>
    </>
  );
}

// ─── Единый модал создания/редактирования клиента ─────────────────────────────
// client = null → создание; client = { id, ... } → редактирование
export default function ClientModal({ client, onClose, onSave, initialData, focusField }) {
  const { t, i18n } = useTranslation();
  const { ct } = useCountryText();
  const isEdit = !!client?.id;

  const [form, setForm] = useState(
    isEdit
      ? { first_name: client.first_name ?? splitName(client.name).first, last_name: client.last_name ?? splitName(client.name).last, phone: client.phone || '', email: client.email || '', notes: client.notes || '', birthday: client.birthday || '', comm_lang: client.comm_lang === 'en' ? 'en' : 'ru' }
      : { first_name: splitName(initialData?.name || '').first, last_name: splitName(initialData?.name || '').last, phone: initialData?.phone || '', email: initialData?.email || '', notes: initialData?.notes || '', birthday: initialData?.birthday || '', comm_lang: uiDocLang() }
  );

  // Аватар-стейт: для edit — из client; для new — null пока не выберут файл
  const [avatarUrl, setAvatarUrl] = useState(client?.avatar_url || null);
  const [avatarThumbUrl, setAvatarThumbUrl] = useState(client?.avatar_thumb_url || null);
  const [avatarR2Key, setAvatarR2Key] = useState(client?.avatar_r2_key || null);
  const [avatarThumbR2Key, setAvatarThumbR2Key] = useState(client?.avatar_thumb_r2_key || null);
  // При создании — держим сжатые блобы локально до Save
  const [pendingBlobs, setPendingBlobs] = useState(null); // { full, thumb }
  const [previewUrl, setPreviewUrl] = useState(null);     // blob URL для превью
  const [uploading, setUploading] = useState(false);

  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Автофокус на нужном поле (напр. открыли карточку, чтобы быстро добавить email).
  const emailRef = useRef(null);
  useEffect(() => {
    if (focusField === 'email' && emailRef.current) {
      // Фокус без автоскролла, показ — одним вызовом: голый focus() прокручивал
      // модал сам и обрывал smooth-анимацию scrollIntoView. См. utils/scroll.
      focusRow(emailRef.current, emailRef.current);
    }
  }, [focusField]);

  // Черновик: только создание без префилла из адресной книги
  const { restored, isDirty, clearDraft, resetForm } = useFormDraft({
    scope: 'client:new',
    form, setForm, enabled: !isEdit && !initialData,
  });
  const guardedClose = useUnsavedGuard(onClose, isDirty, t('common.draftCloseConfirm'), 'client');
  const handleCancel = () => { clearDraft('cancel'); onClose(); };

  // Фото из адресной книги → сжатие → превью + отложенная загрузка (флоу new)
  const applyPhotoBlob = useCallback(async (blob) => {
    try {
      const [full, thumb] = await Promise.all([
        compressImage(blob, 800, 0.85),
        compressImage(blob, 300, 0.85),
      ]);
      setPendingBlobs({ full, thumb });
      setPreviewUrl(p => { if (p) URL.revokeObjectURL(p); return URL.createObjectURL(full); });
    } catch { /* фото не критично — продолжаем без него */ }
  }, []);

  // Префилл фото при открытии модала со страницы Клиенты (_initialData)
  useEffect(() => {
    if (!isEdit && initialData?.photoBlob) applyPhotoBlob(initialData.photoBlob);
    // eslint-disable-next-line
  }, []);

  // Скрываем FAB пока модал открыт
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('fab-hide'));
    return () => window.dispatchEvent(new CustomEvent('fab-show'));
  }, []);

  const initials = ([form.first_name, form.last_name].filter(Boolean).join(' ') || client?.name || '?')
    .split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  // Пользователь выбрал файл — сжимаем и показываем превью (без загрузки)
  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (isEdit) {
      // При редактировании — грузим сразу
      setUploading(true);
      try {
        const [fullBlob, thumbBlob] = await Promise.all([
          compressImage(file, 800, 0.85),
          compressImage(file, 300, 0.85),
        ]);
        const result = await uploadAvatar(client.id, fullBlob, thumbBlob);
        setAvatarUrl(result.avatar_url);
        setAvatarThumbUrl(result.avatar_thumb_url);
        setAvatarR2Key(result.avatar_r2_key);
        setAvatarThumbR2Key(result.avatar_thumb_r2_key);
      } catch (err) {
        toast.error(err.message);
      } finally {
        setUploading(false);
      }
    } else {
      // При создании — только сжимаем и показываем превью
      try {
        const [fullBlob, thumbBlob] = await Promise.all([
          compressImage(file, 800, 0.85),
          compressImage(file, 300, 0.85),
        ]);
        setPendingBlobs({ full: fullBlob, thumb: thumbBlob });
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(URL.createObjectURL(fullBlob));
      } catch (err) {
        toast.error(err.message);
      }
    }
  };

  const handleDeleteAvatar = () => {
    setAvatarUrl(null);
    setAvatarThumbUrl(null);
    setAvatarR2Key(null);
    setAvatarThumbR2Key(null);
    setPendingBlobs(null);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const saved = isEdit
        ? await api.updateClient(client.id, form)
        : await api.createClient(form);

      let result = { ...saved, avatar_url: avatarUrl, avatar_thumb_url: avatarThumbUrl, avatar_r2_key: avatarR2Key, avatar_thumb_r2_key: avatarThumbR2Key };

      // При создании — теперь есть id, загружаем отложенное фото
      if (!isEdit && pendingBlobs) {
        try {
          const avatarResult = await uploadAvatar(saved.id, pendingBlobs.full, pendingBlobs.thumb);
          result = { ...result, ...avatarResult };
        } catch (err) {
          toast.error(t('clients.createdNoPhoto') + ': ' + err.message);
        }
      }

      clearDraft('saved');
      onSave(result);
      toast.success(isEdit ? t('clients.updated') : t('clients.added'));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" {...backdropClose(guardedClose)}>
      <div className="modal">
        <div className="modal-header">
          <h3>{isEdit ? t('clients.modalEdit') : t('clients.modalNew')}</h3>
          <button className="modal-close" onClick={guardedClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ paddingTop: 12, paddingBottom: 8 }}>
            {restored && <DraftRestoredBanner posthog="client_draft" onClear={() => { clearDraft('manual'); resetForm(); }} />}
            {/* Аватар — всегда */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
              <ClientAvatar
                clientId={client?.id || null}
                avatarUrl={avatarUrl}
                previewUrl={previewUrl}
                onFileSelected={handleFileSelected}
                onDeleted={handleDeleteAvatar}
                uploading={uploading}
                size={200}
                initials={initials}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label">{t('clients.fieldFirstName')}</label>
              <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
                <input className="form-input" style={{ flex: 1 }} value={form.first_name} onChange={e => set('first_name', e.target.value)} required placeholder={t('clients.firstNamePlaceholder')} data-posthog="client_first_name" />
                {!isEdit && (
                  <ModalContactsButton
                    onFill={(data) => {
                      const split = splitName(data.name);
                      setForm(f => ({
                        ...f,
                        first_name: split.first || f.first_name,
                        last_name: split.last || f.last_name,
                        phone: data.phone || f.phone,
                        email: data.email || f.email,
                        birthday: data.birthday || f.birthday,
                      }));
                      if (data.photoBlob) applyPhotoBlob(data.photoBlob);
                    }}
                  />
                )}
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label">{t('clients.fieldLastName')}</label>
              <input className="form-input" value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder={t('clients.lastNamePlaceholder')} data-posthog="client_last_name" />
            </div>
            <div className="form-row" style={{ marginBottom: 0 }}>
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="form-label">{t('clients.fieldPhone')}</label>
                <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder={ct('clients.phonePlaceholder')} />
              </div>
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="form-label">{t('clients.fieldEmail')}</label>
                <input ref={emailRef} className="form-input" type="text" inputMode="email" autoComplete="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="ivan@email.com" />
              </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label">{t('clients.fieldCommLang')}</label>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                {['ru', 'en'].map(l => (
                  <button key={l} type="button"
                    className={`btn btn-sm ${(form.comm_lang || 'ru') === l ? 'btn-green' : 'btn-ghost'}`}
                    onClick={() => set('comm_lang', l)}>
                    {l === 'ru' ? 'Русский' : 'English'}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{t('clients.commLangHint')}</div>
            </div>
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label">{t('clients.fieldBirthday')}</label>
              <DateInput value={form.birthday} onChange={v => set('birthday', v)} />
            </div>
            <div className="form-group" style={{ marginBottom: 4 }}>
              <label className="form-label">{t('clients.fieldNotes')}</label>
              <input className="form-input" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder={t('clients.notesPlaceholder')} />
              <TranslationHint record={client} field="notes" uiLang={i18n.language} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>{t('clients.cancel')}</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" /> : t('clients.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
